import React, { useState } from 'react';
import { BusinessPlan } from '../../types/businessPlan';

// API base URL - adjust this to match your backend URL
const API_BASE_URL = 'http://localhost:5000/api';

interface BusinessPlanPDFExportProps {
  plan: BusinessPlan;
}

interface ThemeOption {
  id: string;
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    background: string;
  };
}

interface ChartData {
  id: string;
  config: any; // Chart.js config object
}

const BusinessPlanPDFExport: React.FC<BusinessPlanPDFExportProps> = ({ plan }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [customColors, setCustomColors] = useState({
    primary: '#1a73e8',
    secondary: '#f1f3f4',
    accent: '#fbbc04',
    text: '#333333',
    background: '#ffffff'
  });

  // Predefined theme options
  const themeOptions: ThemeOption[] = [
    { 
      id: 'default', 
      name: 'Professional Blue', 
      colors: {
        primary: '#1a73e8',
        secondary: '#f1f3f4',
        accent: '#fbbc04',
        text: '#333333',
        background: '#ffffff'
      }
    },
    { 
      id: 'elegant', 
      name: 'Elegant Green', 
      colors: {
        primary: '#0f8b8d',
        secondary: '#f8f9fa',
        accent: '#ffc107',
        text: '#2e3440',
        background: '#ffffff'
      }
    },
    { 
      id: 'modern', 
      name: 'Modern Gray', 
      colors: {
        primary: '#5f6368',
        secondary: '#f1f3f4',
        accent: '#ea4335',
        text: '#202124',
        background: '#ffffff'
      }
    },
    { 
      id: 'bold', 
      name: 'Bold Red', 
      colors: {
        primary: '#db4437',
        secondary: '#f5f5f5',
        accent: '#4285f4',
        text: '#333333',
        background: '#ffffff'
      }
    },
    { 
      id: 'custom', 
      name: 'Custom Colors', 
      colors: customColors
    }
  ];

  // Completely remove ALL charts and graphs from the DOM
  const getChartData = async (): Promise<string> => {
    console.log('AUTHORIZED: Completely removing ALL charts and graphs from PDF export');
    
    try {
      // Find all chart containers using various selectors to ensure we find them all
      const allChartContainers = document.querySelectorAll(
        '.chart-container, [data-chart], [id*="chart"], .chart, ' + 
        '[class*="chart"], [class*="Chart"], canvas, [id*="Canvas"], ' +
        '[class*="graph"], [class*="Graph"], [id*="graph"], [id*="Graph"], ' +
        'svg, [class*="visualization"], [id*="visualization"]'
      );
      
      console.log(`Found ${allChartContainers.length} chart/graph containers to remove`);
      
      // Remove all chart containers
      allChartContainers.forEach(container => {
        // Check if this is a container that should be removed
        const shouldRemove = (
          // If it's directly a canvas or SVG
          container.tagName.toLowerCase() === 'canvas' || 
          container.tagName.toLowerCase() === 'svg' ||
          // Or it contains a canvas or SVG
          container.querySelector('canvas, svg') ||
          // Or it has chart/graph in its class or ID
          (container.id && (
            container.id.toLowerCase().includes('chart') || 
            container.id.toLowerCase().includes('graph')
          )) ||
          (container.className && (
            container.className.toLowerCase().includes('chart') ||
            container.className.toLowerCase().includes('graph')
          ))
        );
        
        if (shouldRemove && container.parentNode) {
          container.parentNode.removeChild(container);
          console.log('Removed chart/graph container:', container);
        }
      });
      
      // Also remove any titles that might be associated with charts
      const chartTitles = document.querySelectorAll(
        '[class*="chart-title"], [id*="chart-title"], ' +
        'h3:contains("Chart"), h4:contains("Chart"), ' +
        'h3:contains("Graph"), h4:contains("Graph")'
      );
      
      chartTitles.forEach(title => {
        if (title.parentNode) {
          title.parentNode.removeChild(title);
          console.log('Removed chart/graph title:', title);
        }
      });
      
      // Look for any empty containers that might have held charts
      const emptyContainers = document.querySelectorAll('div:empty, section:empty');
      emptyContainers.forEach(container => {
        if (container.parentNode) {
          container.parentNode.removeChild(container);
          console.log('Removed empty container:', container);
        }
      });
      
      return 'All charts and graphs removed successfully';
    } catch (error) {
      console.error('Error removing charts and graphs:', error);
      return 'Error removing charts and graphs';
    }
  };

  const prepareContentForPDF = (contentElement: HTMLElement): string => {
    // Clone the element to avoid modifying the original DOM
    const contentClone = contentElement.cloneNode(true) as HTMLElement;
    
    console.log('Preparing content for PDF export - fixing all remaining issues');
    
    try {
      // Extract all existing styles
      const extractOriginalStyles = () => {
        let originalStyles = '';
        try {
          originalStyles = Array.from(document.styleSheets)
            .filter(sheet => {
              try {
                return sheet.cssRules.length > 0;
              } catch (e) {
                return false;
              }
            })
            .map(sheet => {
              return Array.from(sheet.cssRules)
                .map(rule => rule.cssText)
                .join('\n');
            }).join('\n');
        } catch (e) {
          console.warn('Error extracting original styles:', e);
        }
        return originalStyles;
      };

      // Remove section/page numbers that show up in the PDF
      const removeNumbersAndLabels = () => {
        console.log('Removing section numbers and page labels');
        
        // Find and remove section number elements
        const numberElements = contentClone.querySelectorAll(
          '.section-number, .page-number, [class*="number"], .number, ' + 
          'span[data-section-number], [aria-label*="number"], ' + 
          'div.number, span.number'
        );
        
        numberElements.forEach(el => {
          if (el.parentNode) {
            console.log('Removing number element:', el.textContent);
            el.parentNode.removeChild(el);
          }
        });
        
        // Also look for headers that start with numbers and clean them
        const headers = contentClone.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headers.forEach(header => {
          if (header.textContent) {
            // If header starts with a number (like "1. Introduction")
            if (/^\d+\s*[.:]\s*/.test(header.textContent)) {
              // Remove the number prefix
              header.textContent = header.textContent.replace(/^\d+\s*[.:]\s*/, '');
              console.log('Cleaned numbered header:', header.textContent);
            }
          }
        });
      };
      
      // 1. FIX BLANK FIRST PAGE: Create a proper title page if none exists
      const fixTitlePage = () => {
        console.log('Fixing blank first page by ensuring title page exists');
        
        // Check for existing title page
        let titlePage = contentClone.querySelector('.title-page, #title-page, .cover-page, #cover-page');
        
        // If no title page exists and we have a plan, create one
        if (!titlePage && plan) {
          titlePage = document.createElement('div');
          titlePage.id = 'title-page';
          titlePage.classList.add('title-page');
          
          // Style the title page
          const titlePageEl = titlePage as HTMLElement;
          titlePageEl.style.display = 'flex';
          titlePageEl.style.flexDirection = 'column';
          titlePageEl.style.justifyContent = 'center';
          titlePageEl.style.alignItems = 'center';
          titlePageEl.style.height = '100vh';
          titlePageEl.style.margin = '0';
          titlePageEl.style.padding = '2cm';
          titlePageEl.style.textAlign = 'center';
          titlePageEl.style.pageBreakAfter = 'always';
          titlePageEl.style.breakAfter = 'page';
          
          // Create and add title
          const title = document.createElement('h1');
          title.textContent = plan.title || 'Business Plan';
          (title as HTMLElement).style.fontSize = '36pt';
          (title as HTMLElement).style.color = '#000';
          (title as HTMLElement).style.marginBottom = '2cm';
          titlePage.appendChild(title);
          
          // Add business idea name if available
          if (plan.businessIdea) {
            const subtitle = document.createElement('h2');
            subtitle.textContent = plan.businessIdea.title || '';
            (subtitle as HTMLElement).style.fontSize = '24pt';
            (subtitle as HTMLElement).style.marginBottom = '1cm';
            titlePage.appendChild(subtitle);
          }
          
          // Add date
          const date = document.createElement('p');
          date.textContent = new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
          (date as HTMLElement).style.fontSize = '14pt';
          (date as HTMLElement).style.marginTop = '2cm';
          titlePage.appendChild(date);
          
          // Add as first element
          contentClone.insertBefore(titlePage, contentClone.firstChild);
          console.log('Created new title page to fix blank first page');
        } else if (titlePage instanceof HTMLElement) {
          // Ensure existing title page has proper styles
          titlePage.style.display = 'flex';
          titlePage.style.flexDirection = 'column';
          titlePage.style.justifyContent = 'center';
          titlePage.style.alignItems = 'center';
          titlePage.style.height = '100vh';
          titlePage.style.pageBreakAfter = 'always';
          titlePage.style.breakAfter = 'page';
          console.log('Fixed existing title page');
        }
      };
      
      // 2. FIX DUPLICATE HEADINGS: Remove duplicate headings in the same section
      const fixDuplicateHeadings = () => {
        console.log('Fixing duplicate headings');
        
        // Track headings we've seen in each section
        const headingMap = new Map<string, Set<string>>();
        
        // Find all headings
        const headings = contentClone.querySelectorAll('h1, h2, h3, h4, h5, h6');
        
        // Process each heading
        headings.forEach(heading => {
          if (!(heading instanceof HTMLElement)) return;
          
          // Get heading text and skip if empty
          const headingText = heading.textContent?.trim() || '';
          if (!headingText) return;
          
          // Find the parent section
          const parentSection = heading.closest('section, [id^="section-"], .section');
          const sectionId = parentSection?.id || 'default';
          
          // Init set of headings for this section if not exists
          if (!headingMap.has(sectionId)) {
            headingMap.set(sectionId, new Set<string>());
          }
          
          // Get the set of headings for this section
          const sectionHeadings = headingMap.get(sectionId)!;
          
          // If we've seen this heading in this section, remove it
          if (sectionHeadings.has(headingText)) {
            console.log(`Removing duplicate heading "${headingText}" in section ${sectionId}`);
            if (heading.parentNode) {
              heading.parentNode.removeChild(heading);
            }
          } else {
            // First time seeing this heading, add to set
            sectionHeadings.add(headingText);
          }
        });
      };

      // 3. REMOVE BLANK PAGES: Find and remove empty sections or divs
      const removeBlankPages = () => {
        console.log('Removing blank pages');
        
        // First, remove completely empty sections
        const sections = contentClone.querySelectorAll('section, div[id^="section-"], .section, div[class*="section"]');
        sections.forEach(section => {
          if (section instanceof HTMLElement) {
            // Check if section has any visible content
            const hasVisibleContent = Array.from(section.querySelectorAll('*')).some(el => {
              if (el instanceof HTMLElement) {
                // Skip the section element itself
                if (el === section) return false;
                
                // Skip purely structural elements
                if (el.tagName === 'STYLE' || el.tagName === 'SCRIPT') return false;
                
                // Skip empty elements
                if (!el.textContent && !el.querySelector('img') && !el.getAttribute('data-content')) return false;
                
                // Check if element has only whitespace
                if (el.textContent && el.textContent.trim() === '') return false;
                
                return true;
              }
              return false;
            });
            
            // If section has no visible content, remove it
            if (!hasVisibleContent) {
              console.log('Removing empty section/page');
              if (section.parentNode) {
                section.parentNode.removeChild(section);
              }
            }
          }
        });
        
        // Also look for page breaks followed by empty content
        const pageBreaks = contentClone.querySelectorAll('[style*="page-break"], [style*="break-before"], [style*="break-after"]');
        pageBreaks.forEach(breakEl => {
          // Check if the next element is empty
          let nextEl = breakEl.nextElementSibling;
          if (nextEl && nextEl instanceof HTMLElement) {
            // If next element is empty, remove the page break
            if (!nextEl.textContent || nextEl.textContent.trim() === '') {
              console.log('Removing page break before empty content');
              if (breakEl instanceof HTMLElement) {
                breakEl.style.pageBreakAfter = 'auto';
                breakEl.style.pageBreakBefore = 'auto';
                breakEl.style.breakAfter = 'auto';
                breakEl.style.breakBefore = 'auto';
              }
            }
          }
        });
      };
      
      // Add styling specific to the PDF
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        ${extractOriginalStyles()}
        
        /* PDF-specific styles */
        @page {
          size: A4;
          margin: 2cm;
        }
        
        /* Remove any automatic page numbers */
        @page :first { margin-bottom: 2cm; }
        
        /* Title page styling */
        .title-page, #title-page, .cover-page, #cover-page {
          page-break-after: always !important;
          break-after: page !important;
          height: 100vh !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          text-align: center !important;
        }
        
        /* Hide all page/section numbers in PDF */
        .section-number, .page-number, [class*="number"], .number, 
        span[data-section-number], [aria-label*="number"], 
        div.number, span.number {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Fix spacing between sections */
        section, .section, div[id^="section-"] {
          margin-top: 1cm !important;
          margin-bottom: 1cm !important;
          padding-top: 0.5cm !important;
        }
        
        /* Section breaks on sections with content */
        section:not(:empty):not(:first-of-type), 
        div[id^="section-"]:not(:empty):not(:first-of-type), 
        .section:not(:empty):not(:first-of-type) {
          page-break-before: always !important;
          break-before: page !important;
        }
        
        /* Avoid page breaks after headings */
        h1, h2, h3, h4, h5, h6 {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }
      `;
      contentClone.appendChild(styleEl);
      
      // Apply our fixes in sequence
      fixTitlePage();
      fixDuplicateHeadings();
      removeNumbersAndLabels();
      removeBlankPages();
      
    } catch (error) {
      console.error('Error preparing PDF content:', error);
    }
    
    // Remove UI controls and hidden elements
    const elementsToRemove = contentClone.querySelectorAll(
      '.edit-button, button.edit, button[aria-label*="edit"], ' +
      '.ui-only, .no-print, .edit-controls, button:not([data-include-in-pdf="true"]), ' +
      '[style*="display: none"], [style*="visibility: hidden"], [hidden], ' +
      // Additional selectors to remove numbers and empty elements
      '.section-number, .page-number, [class*="number"], .number, ' + 
      'div:empty, p:empty, span:empty, [aria-label*="number"]'
    );
    
    elementsToRemove.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    
    // Make sure everything is visible and properly formatted
    Array.from(contentClone.querySelectorAll('*')).forEach(el => {
      if (el instanceof HTMLElement) {
        // Make tables visible
        if (el.tagName.toLowerCase() === 'table') {
          el.style.display = 'table';
          el.setAttribute('border', '1');
        } else if (el.tagName.toLowerCase() === 'tr') {
          el.style.display = 'table-row';
        } else if (el.tagName.toLowerCase() === 'td' || el.tagName.toLowerCase() === 'th') {
          el.style.display = 'table-cell';
        } else if (el.style.display === 'none') {
          el.style.display = 'block';
        }
        
        // Ensure visibility
        el.style.visibility = 'visible';
        if (el.hasAttribute('hidden')) {
          el.removeAttribute('hidden');
        }
      }
    });
    
    return contentClone.innerHTML;
  };

  const fetchPdf = async () => {
    try {
      setIsExporting(true);
      setLoading(true);
      setError(null);
      
      // Convert chart canvases to images before preparing the content
      await getChartData();
      
      // Get the business plan content element - Try multiple selectors
      const contentElement = document.getElementById('business-plan-document') || 
                             document.querySelector('.business-plan-document') ||
                             document.querySelector('.document-content') ||
                             document.querySelector('.business-plan');
                             
      if (!contentElement) {
        throw new Error('Could not find business plan content element. Please ensure the plan is visible.');
      }
      
      console.log('Found content element:', contentElement.tagName, contentElement.id || 'no-id', 
        'with', contentElement.children.length, 'children');
      
      // Prepare the content by removing UI elements and formatting for PDF
      const content = prepareContentForPDF(contentElement);
      
      // Get the session token for authentication
      // const { data: { session } } = await supabase.auth.getSession();
      // if (!session) {
      //   throw new Error('You must be logged in to export a PDF');
      // }
      const token = 'your_token'; // Replace with your token or authentication logic
      
      // Create the request options
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          htmlContent: content, // Fix parameter name to match backend expectation
          title: plan.title,
          theme: selectedTheme,
          colors: customColors,
          charts: [] // Include empty charts array to satisfy backend requirement
        })
      };
      
      // Make the request to generate the PDF
      const response = await fetch(`${API_BASE_URL}/business-plans/generate-pdf`, options);
      
      if (!response.ok) {
        throw new Error(`PDF generation failed: ${response.statusText}`);
      }
      
      // Get the PDF as a blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link and click it to download the PDF
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plan.title || 'Business Plan'}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setIsExporting(false);
      setLoading(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setIsExporting(false);
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (loading) return;
    
    setIsExporting(true);
    await fetchPdf();
    setIsExporting(false);
  };

  return (
    <div className="business-plan-export">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Theme</label>
        <select 
          value={selectedTheme}
          onChange={(e) => setSelectedTheme(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          disabled={isExporting}
        >
          {themeOptions.map(theme => (
            <option key={theme.id} value={theme.id}>{theme.name}</option>
          ))}
        </select>
      </div>
      
      {selectedTheme === 'custom' && (
        <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Custom Colors</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Primary Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.primary}
                  onChange={(e) => setCustomColors({...customColors, primary: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.primary}
                  onChange={(e) => setCustomColors({...customColors, primary: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Secondary Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.secondary}
                  onChange={(e) => setCustomColors({...customColors, secondary: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.secondary}
                  onChange={(e) => setCustomColors({...customColors, secondary: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Accent Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.accent}
                  onChange={(e) => setCustomColors({...customColors, accent: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.accent}
                  onChange={(e) => setCustomColors({...customColors, accent: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Text Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.text}
                  onChange={(e) => setCustomColors({...customColors, text: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.text}
                  onChange={(e) => setCustomColors({...customColors, text: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="my-2 p-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}
      
      <button
        onClick={handleExport}
        disabled={isExporting}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-150 ease-in-out disabled:opacity-50"
      >
        {isExporting ? (
          <>
            <span className="inline-block animate-spin mr-2">⟳</span>
            Generating PDF...
          </>
        ) : (
          'Export PDF'
        )}
      </button>
    </div>
  );
};

// Add Chart type to the global Window interface
declare global {
  interface Window {
    Chart?: {
      getChart?: (canvas: HTMLCanvasElement) => any;
    };
  }
}

export default BusinessPlanPDFExport;
